import { useCallback, useMemo, type PropsWithChildren } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import { isValidLayoutSection, type PanelConfig } from '../../types/LayoutConfig';
import { useAppLayoutConfig } from '../AppLayoutConfig/AppLayoutConfigContext';
import { MobileLayoutPanelContext } from './MobileLayoutPanelContext';

export function MobileLayoutPanelProvider({ children }: PropsWithChildren) {
  const { layoutId, panelId } = useParams<{ layoutId?: string; panelId?: string }>();
  const sectionMatch = useMatch('/:sectionId/*');
  const sectionId = sectionMatch?.params.sectionId;

  const layoutSection = useMemo(() => (isValidLayoutSection(sectionId) ? sectionId : undefined), [sectionId]);
  const {
    layoutConfig: defaultLayoutConfig,
    layoutConfigsMap,
    updatePanelParams: updateStoredPanelParams,
  } = useAppLayoutConfig(layoutSection);

  const currentLayoutConfig = layoutId != null ? layoutConfigsMap?.[layoutId] : defaultLayoutConfig;

  const updatePanelParams = useCallback(
    (paramsPatch: Partial<PanelConfig>) => {
      if (panelId == null || layoutSection == null) {
        return;
      }

      const targetLayoutId = layoutId ?? currentLayoutConfig?.id;
      if (targetLayoutId == null) {
        return;
      }

      updateStoredPanelParams(targetLayoutId, panelId, paramsPatch);
    },
    [panelId, layoutSection, layoutId, currentLayoutConfig, updateStoredPanelParams]
  );

  const contextValue = useMemo(
    () => ({
      updatePanelParams,
    }),
    [updatePanelParams]
  );

  return <MobileLayoutPanelContext.Provider value={contextValue}>{children}</MobileLayoutPanelContext.Provider>;
}
